/** Shared date formatting utilities used by DateRangePicker and preset helpers. */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const

/** ISO calendar date for APIs / query params: YYYY-MM-DD. */
export function formatCalendarDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Display form for UI inputs: DD-MM-YYYY. */
export function formatDisplayDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${day}-${m}-${y}`
}

/** Display datetime: DD-MM-YYYY, HH:mm:ss (local). */
export function formatDisplayDateTime(d: Date): string {
  if (Number.isNaN(d.getTime())) return "—"
  const h = String(d.getHours()).padStart(2, "0")
  const min = String(d.getMinutes()).padStart(2, "0")
  const s = String(d.getSeconds()).padStart(2, "0")
  return `${formatDisplayDate(d)}, ${h}:${min}:${s}`
}

/** Rewrite bare YYYY-MM-DD tokens in text to DD-MM-YYYY for display. */
export function rewriteIsoDatesInText(text: string): string {
  return text.replace(
    /\b(\d{4})-(\d{2})-(\d{2})\b/g,
    (_m, y: string, mo: string, d: string) => `${d}-${mo}-${y}`,
  )
}

/**
 * Cap an inclusive ISO date range to at most `maxDays` days.
 * Orders start≤end, then keeps start and clamps end when the span is too long.
 */
export function clampIsoRangeToMaxDays(
  startIso: string,
  endIso: string,
  maxDays: number,
): { startDate: string; endDate: string } {
  const a = new Date(startIso + "T00:00:00")
  const b = new Date(endIso + "T00:00:00")
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return { startDate: startIso, endDate: endIso }
  }
  const start = a <= b ? a : b
  let end = a <= b ? b : a
  const maxEnd = new Date(start)
  maxEnd.setDate(maxEnd.getDate() + Math.max(maxDays, 1) - 1)
  if (end > maxEnd) end = maxEnd
  return {
    startDate: formatCalendarDate(start),
    endDate: formatCalendarDate(end),
  }
}

function labelFromRange(s: Date, e: Date): string {
  const sameDay =
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    s.getDate() === e.getDate()
  if (sameDay) {
    return `${MONTHS[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}`
  }
  if (s.getFullYear() === e.getFullYear()) {
    if (s.getMonth() === e.getMonth()) {
      return `${MONTHS[s.getMonth()]} ${s.getDate()}-${e.getDate()}, ${s.getFullYear()}`
    }
    return `${MONTHS[s.getMonth()]} ${s.getDate()} - ${MONTHS[e.getMonth()]} ${e.getDate()}, ${s.getFullYear()}`
  }
  return `${MONTHS[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()} - ${MONTHS[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`
}

export function labelFromIsoRange(startIso: string, endIso: string): string {
  const s = new Date(startIso + "T00:00:00")
  const e = new Date(endIso + "T00:00:00")
  return labelFromRange(s, e)
}
